// Event payment-provider settings embedded in Store Settings.
// Loads the event's Stripe Connect status, starts/continues hosted onboarding,
// refreshes account readiness, and reports success/errors to the parent page.
// Payment funds continue to flow directly to the event's connected account.

import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { logger } from "../../utils/logger";

const EMPTY_STRIPE_STATUS = {
  connected: false,
  onboardingComplete: false,
  chargesEnabled: false,
  payoutsEnabled: false,
  detailsSubmitted: false,
  accountId: "",
};

const PaymentSettingsPanel = ({ eventId, eventslug, onNotice, onError }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState("none");
  const [stripeStatus, setStripeStatus] = useState(EMPTY_STRIPE_STATUS);

  useEffect(() => {
    const loadPaymentSettings = async () => {
      if (!eventId) return;

      try {
        setLoading(true);
        const snap = await getDoc(doc(db, "events", eventId));

        if (!snap.exists()) {
          onError?.("Event was not found.");
          return;
        }

        const payments = snap.data()?.payments || {};
        setPaymentProvider(payments.provider || "none");
        setStripeStatus({
          connected: payments.stripe?.connected === true,
          onboardingComplete: payments.stripe?.onboardingComplete === true,
          chargesEnabled: payments.stripe?.chargesEnabled === true,
          payoutsEnabled: payments.stripe?.payoutsEnabled === true,
          detailsSubmitted: payments.stripe?.detailsSubmitted === true,
          accountId: payments.stripe?.accountId || "",
        });
      } catch (error) {
        logger.error("Failed to load payment settings", { value: error });
        onError?.("Failed to load payment settings.");
      } finally {
        setLoading(false);
      }
    };

    loadPaymentSettings();
  }, [eventId, onError]);

  const connectStripe = async () => {
    if (!eventId || !eventslug) return;

    try {
      setSaving(true);
      onError?.(null);
      const createOnboardingLink = httpsCallable(
        functions,
        "createStripeOnboardingLink"
      );
      const currentUrl = `${window.location.origin}/${eventslug}/store_settings`;
      const result = await createOnboardingLink({
        eventId,
        returnUrl: `${currentUrl}?stripe_return=1`,
        refreshUrl: `${currentUrl}?stripe_refresh=1`,
      });

      if (!result?.data?.url) {
        throw new Error("Stripe did not return an onboarding link.");
      }

      window.location.href = result.data.url;
    } catch (error) {
      logger.error("Stripe onboarding failed", { value: error });
      onError?.(error?.message || "Could not start Stripe onboarding.");
      setSaving(false);
    }
  };

  const refreshStripeStatus = async () => {
    if (!eventId) return;

    try {
      setSaving(true);
      onError?.(null);
      const refreshStatus = httpsCallable(functions, "refreshStripeAccountStatus");
      const result = await refreshStatus({ eventId });
      const data = result?.data || {};

      setStripeStatus((previous) => ({
        ...previous,
        connected: true,
        accountId: data.accountId || previous.accountId,
        chargesEnabled: data.chargesEnabled === true,
        payoutsEnabled: data.payoutsEnabled === true,
        detailsSubmitted: data.detailsSubmitted === true,
        onboardingComplete: data.detailsSubmitted === true,
      }));
      setPaymentProvider("stripe");
      onNotice?.("Stripe status refreshed.");
    } catch (error) {
      logger.error("Failed to refresh Stripe status", { value: error });
      onError?.(error?.message || "Could not refresh Stripe status.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="alert alert-light border">Loading payment settings...</div>;
  }

  const stripeReady =
    stripeStatus.connected &&
    stripeStatus.chargesEnabled &&
    stripeStatus.payoutsEnabled;

  return (
    <div className="card shadow-sm border-0 mt-5" id="payment-settings">
      <div className="card-header bg-white">
        <strong>Payment Settings</strong>
      </div>
      <div className="card-body">
        <p className="text-muted">
          Connect the event&apos;s merchant account for Auctions, Store, Tickets, Sponsors, and Vendors.
          Payments go directly to the connected event account.
        </p>

        <div className="row g-3 mb-4">
          <div className="col-12 col-md-6">
            <div className="border rounded p-3 h-100">
              <div className="fw-bold">Provider</div>
              <div>{paymentProvider === "stripe" ? "Stripe" : "Disabled"}</div>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="border rounded p-3 h-100">
              <div className="fw-bold">Connection</div>
              <div className={stripeReady ? "text-success" : "text-warning"}>
                {stripeReady ? "Ready to accept payments" : "Setup required"}
              </div>
            </div>
          </div>
        </div>

        {stripeStatus.connected && (
          <div className="row g-3 mb-4">
            {[
              ["Details Submitted", stripeStatus.detailsSubmitted],
              ["Charges Enabled", stripeStatus.chargesEnabled],
              ["Payouts Enabled", stripeStatus.payoutsEnabled],
            ].map(([label, ready]) => (
              <div className="col-12 col-md-4" key={label}>
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold">{label}</div>
                  <div className={ready ? "text-success" : "text-warning"}>
                    {ready ? "Yes" : "Not Yet"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={connectStripe}
            disabled={saving}
          >
            {stripeStatus.connected ? "Continue Stripe Setup" : "Connect Stripe"}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={refreshStripeStatus}
            disabled={saving || !stripeStatus.connected}
          >
            Refresh Stripe Status
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettingsPanel;
